import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { AllBreakTypes } from '@sdk/settings/types';
import { TimesheetEntry, TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { TimesheetEntryPayloadEntry } from '@sdk/timesheets/api/apiTypes';

import {
  TimeClockEntry,
  BreakEntry,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { breakDownTimesheetsToGroups } from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';
import { EditFormFieldValues } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.types';
import { ShiftWithLocationTimezone } from '@web/timeClock/ClockOutModal/clockOutModal.types';
import { getClockInClockOutDates } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';
import { formatDateToTimestampString } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.utils';
import mapBreakEntryToTimesheetEntries from '@web/timesheets/EditTimesheetModal/utils/mapBreakEntryToTimesheetEntries';

export const mapTimesheetEntriesToTimeClockEntriesWithDates = (
  timesheetEntries: TimesheetEntry[]
) => {
  console.log(breakDownTimesheetsToGroups);
  const timesheetGroups = breakDownTimesheetsToGroups(timesheetEntries);
  const timeClockEntries: TimeClockEntry[] = [];
  /**
   * We're about to start looping through `TimesheetEntries` that contains single action.
   * Each entry reflects only one single part of entire action, so `clockIn` would be one entry
   * and `clockOut` would be another entry. Our goal is to map them to `TimeClockEntry`,
   * which is represented by one record that has both `clockIn`, clockOut and even list of breaks.
   */
  let lastProcessedBreakEntry: BreakEntry;
  let clockInTimeClockEntry: TimeClockEntry;

  timesheetGroups.forEach((timesheetGroup) => {
    timesheetGroup.forEach((timesheetEntry) => {
      if (timesheetEntry.type === TimesheetEntryType.clockIn) {
        clockInTimeClockEntry = {
          key: uuidv4(),
          clockIn: format(timesheetEntry.timestamp, 'HH:mm:ss'),
          clockInEntry: timesheetEntry,
          clockOut: null,
          baseSequenceId: timesheetEntry.baseSequenceId || null,
          breaks: [],
          deleted: false,
        };
        timeClockEntries.push(clockInTimeClockEntry);
      }
    });

    timesheetGroup.forEach((timesheetEntry) => {
      switch (timesheetEntry.type) {
        case TimesheetEntryType.clockOut:
        case TimesheetEntryType.autoClockOut: {
          clockInTimeClockEntry.clockOut = format(timesheetEntry.timestamp, 'HH:mm:ss');
          clockInTimeClockEntry.clockOutEntry = timesheetEntry;
          break;
        }

        case TimesheetEntryType.breakStart: {
          lastProcessedBreakEntry = {
            key: uuidv4(),
            timeClockEntryKey: clockInTimeClockEntry.key,
            start: format(timesheetEntry.timestamp, 'HH:mm:ss'),
            startEntry: timesheetEntry,
            end: null,
            baseSequenceId: timesheetEntry.baseSequenceId || null,
            sequenceId: timesheetEntry.sequenceId || null,
            type: AllBreakTypes[timesheetEntry.automatedBreakType] || AllBreakTypes.standard,
            deleted: false,
          };

          clockInTimeClockEntry.breaks.push(lastProcessedBreakEntry);
          break;
        }

        case TimesheetEntryType.breakEnd: {
          lastProcessedBreakEntry.end = format(timesheetEntry.timestamp, 'HH:mm:ss');
          lastProcessedBreakEntry.endEntry = timesheetEntry;
          break;
        }
      }
    });
  });

  return timeClockEntries;
};

const emptyTimeClockIdData: {
  id: null;
  baseSequenceId: null;
  sequenceId: null;
} = {
  id: null,
  baseSequenceId: null,
  sequenceId: null,
};

export const mapTimesheetFormFieldsToBulkPayload = (
  { date, timeClock }: EditFormFieldValues,
  shift: ShiftWithLocationTimezone
): TimesheetEntryPayloadEntry[] => {
  const timesheetEntries = [];
  timeClock.forEach((timeClockEntry) => {
    let timesheetEntry: TimesheetEntryPayloadEntry;

    const { clockIn, clockOut } = getClockInClockOutDates(
      date,
      timeClockEntry.clockIn,
      timeClockEntry.clockOut
    );

    if (clockIn) {
      // If there is no clockInEntry that means it's new entry (added during edit)
      const idDataSource = timeClockEntry.clockInEntry || emptyTimeClockIdData;
      const { id, baseSequenceId } = idDataSource;

      timesheetEntry = {
        id,
        baseSequenceId,
        timestamp: formatDateToTimestampString(clockIn),
        type: TimesheetEntryType.clockIn,
        user: {
          id: shift.user.id,
        },
        event: {
          id: shift.id,
        },
        deleted: Boolean(timeClockEntry.deleted),
      };
      timesheetEntries.push(timesheetEntry);
    } else if (timeClockEntry.clockInEntry) {
      // clock in was removed
      timesheetEntries.push({
        id: timeClockEntry.clockInEntry.id,
        deleted: true,
      });
    }

    if (clockOut) {
      const { clockInEntry, clockOutEntry } = timeClockEntry;
      const id = clockOutEntry?.id || null;
      const baseSequenceId = clockOutEntry?.baseSequenceId || clockInEntry?.baseSequenceId || null;

      timesheetEntry = {
        id,
        baseSequenceId,
        timestamp: formatDateToTimestampString(clockOut),
        type: TimesheetEntryType.clockOut,
        user: {
          id: shift.user.id,
        },
        event: {
          id: shift.id,
        },
        deleted: Boolean(timeClockEntry.deleted),
      };
      timesheetEntries.push(timesheetEntry);
    } else if (timeClockEntry.clockOutEntry) {
      // clock out was removed
      timesheetEntries.push({
        id: timeClockEntry.clockOutEntry.id,
        deleted: true,
      });
    }

    timeClockEntry.breaks.forEach((breakEntry) => {
      timesheetEntries.push(
        ...mapBreakEntryToTimesheetEntries({ timeClockEntry, breakEntry, clockIn, shift })
      );
    });
  });

  return timesheetEntries;
};
